/**
 * Logger d'erreurs frontend vers /api/v1/log/error (MVP1.2 T6).
 *
 * Pas un Sentry complet — single-user, OVH mutualisé. Le backend rate-limit
 * 30/min/IP et n'écrit qu'en logs/frontend-errors-YYYY-MM-DD.log.
 *
 * Utilisation :
 *   - installGlobalErrorLogger() : appelé une fois au boot (main.tsx)
 *   - logError({ message, error, context }) : usage explicite dans un catch
 *
 * Anti-bombing : ne loggue que les 50 premières erreurs par session (sinon
 * boucle infinie d'erreurs peut spam le serveur).
 */

import { http } from '@/api/http';

const MAX_ERRORS_PER_SESSION = 50;
let errorsSent = 0;

export interface ErrorContext {
  module?: string;
  url?: string;
  extra?: Record<string, unknown>;
}

export async function logError(payload: {
  message: string;
  error?: unknown;
  context?: ErrorContext;
}): Promise<void> {
  if (errorsSent >= MAX_ERRORS_PER_SESSION) return;
  errorsSent++;
  const stack = payload.error instanceof Error ? payload.error.stack : undefined;
  const body: { message: string; stack?: string; context?: ErrorContext } = {
    message: payload.message,
  };
  if (stack !== undefined) body.stack = stack;
  if (payload.context !== undefined) body.context = payload.context;
  try {
    await http.post('/api/v1/log/error', body);
  } catch {
    // ne PAS re-throw : on n'a pas envie de boucler "erreur dans le logger d'erreur"
  }
}

/**
 * Branche window.onerror et window.onunhandledrejection.
 */
export function installGlobalErrorLogger(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (event) => {
    void logError({
      message: event.message,
      error: event.error,
      context: { url: window.location.pathname },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    void logError({
      message: `unhandledrejection: ${message}`,
      error: reason instanceof Error ? reason : undefined,
      context: { url: window.location.pathname },
    });
  });
}
